import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Plus, 
  X, 
  Check,
  Search,
  ArrowRight,
  Database,
  Settings,
  GitBranch,
  AlertTriangle,
  Info,
  Eye,
  Edit3,
  Copy,
  Download
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectorPairBuilder = ({ open, onOpenChange, entities, sources, targets }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSourcePair, setSelectedSourcePair] = useState(null);
  const [selectedTargetPair, setSelectedTargetPair] = useState(null);
  const [generatedSqlScripts, setGeneratedSqlScripts] = useState(null);
  const [generateScriptsOnly, setGenerateScriptsOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // Search states for filtering pairs
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [targetSearchTerm, setTargetSearchTerm] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setSelectedSourcePair(null);
      setSelectedTargetPair(null);
      setGeneratedSqlScripts(null);
      setSourceSearchTerm('');
      setTargetSearchTerm('');
    }
  }, [open]);

  const resetBuilder = () => {
    setCurrentStep(1);
    setSelectedSourcePair(null);
    setSelectedTargetPair(null);
    setGeneratedSqlScripts(null);
    setLoading(false);
    setSourceSearchTerm('');
    setTargetSearchTerm('');
  };

  // Filter entities based on search terms
  const filteredSourceEntities = entities.filter(entity => {
    if (!sourceSearchTerm) return true;
    const searchLower = sourceSearchTerm.toLowerCase();
    return (
      entity.source?.toLowerCase().includes(searchLower) ||
      entity.target?.toLowerCase().includes(searchLower) ||
      entity.entity?.toLowerCase().includes(searchLower) ||
      entity.id?.toString().includes(searchLower)
    );
  });

  const filteredTargetEntities = entities.filter(entity => {
    if (!targetSearchTerm) return true;
    const searchLower = targetSearchTerm.toLowerCase();
    return (
      entity.source?.toLowerCase().includes(searchLower) ||
      entity.target?.toLowerCase().includes(searchLower) ||
      entity.entity?.toLowerCase().includes(searchLower) ||
      entity.id?.toString().includes(searchLower)
    );
  });

  const buildConnectorPair = async () => {
    setLoading(true);
    try {
      // Call new build-connector endpoint with CSV logic
      const response = await axios.post(`${API}/build-connector`, {
        sourcePairId: selectedSourcePair.id,
        targetPairId: selectedTargetPair.id,
        generateScriptsOnly: generateScriptsOnly
      });
      
      if (generateScriptsOnly) {
        setGeneratedSqlScripts(response.data);
        setCurrentStep(3); // Show SQL scripts step
        toast.success('SQL scripts generated successfully using CSV reference logic!');
      } else {
        toast.success('Connector pair created successfully!');
        onOpenChange(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error building connector pair:', error);
      toast.error('Failed to build connector pair: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center space-x-4 mb-6">
      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${currentStep === 1 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
        <span className="text-sm font-medium">1</span>
        <span className="text-sm">Select Pairs</span>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-400" />
      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${currentStep === 2 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
        <span className="text-sm font-medium">2</span>
        <span className="text-sm">Review & Generate</span>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-400" />
      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${currentStep === 3 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
        <span className="text-sm font-medium">3</span>
        <span className="text-sm">SQL Scripts</span>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium" style={{color: '#0f172a'}}>Select Source and Target Reference Pairs</h3>
        <p className="text-sm text-gray-600 mt-1">
          Choose existing connector pairs to use as templates for the new connector pair
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Pair Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm" style={{color: '#0f172a'}}>Source Reference Pair</CardTitle>
            <p className="text-xs text-gray-500">Choose an existing source pair to use as template</p>
            
            {/* Source Search Input */}
            <div className="relative mt-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by source, target, entity, or ID..."
                value={sourceSearchTerm}
                onChange={(e) => setSourceSearchTerm(e.target.value)}
                className="pl-10 text-xs h-8"
              />
              {sourceSearchTerm && (
                <button
                  onClick={() => setSourceSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Results count */}
            <p className="text-xs text-gray-500 mt-1">
              {filteredSourceEntities.length} of {entities.length} pairs
            </p>
          </CardHeader>
          <CardContent className="space-y-3 max-h-80 overflow-y-auto">
            {filteredSourceEntities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No pairs found matching "{sourceSearchTerm}"</p>
                <p className="text-xs">Try a different search term</p>
              </div>
            ) : (
              filteredSourceEntities.map(entity => (
                <div 
                  key={entity.id} 
                  className={`p-3 border rounded cursor-pointer transition-all hover:bg-slate-50 ${
                    selectedSourcePair?.id === entity.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setSelectedSourcePair(entity)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm" style={{color: '#0f172a'}}>
                        {entity.source} → {entity.target}
                      </div>
                      <div className="text-xs text-gray-500">
                        Entity: {entity.entity} | ID: {entity.id}
                      </div>
                    </div>
                    {selectedSourcePair?.id === entity.id && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Target Pair Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm" style={{color: '#0f172a'}}>Target Reference Pair</CardTitle>
            <p className="text-xs text-gray-500">Choose an existing target pair to use as template</p>
            
            {/* Target Search Input */}
            <div className="relative mt-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by source, target, entity, or ID..."
                value={targetSearchTerm}
                onChange={(e) => setTargetSearchTerm(e.target.value)}
                className="pl-10 text-xs h-8"
              />
              {targetSearchTerm && (
                <button
                  onClick={() => setTargetSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Results count */}
            <p className="text-xs text-gray-500 mt-1">
              {filteredTargetEntities.length} of {entities.length} pairs
            </p>
          </CardHeader>
          <CardContent className="space-y-3 max-h-80 overflow-y-auto">
            {filteredTargetEntities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No pairs found matching "{targetSearchTerm}"</p>
                <p className="text-xs">Try a different search term</p>
              </div>
            ) : (
              filteredTargetEntities.map(entity => (
                <div 
                  key={entity.id} 
                  className={`p-3 border rounded cursor-pointer transition-all hover:bg-slate-50 ${
                    selectedTargetPair?.id === entity.id ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}
                  onClick={() => setSelectedTargetPair(entity)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm" style={{color: '#0f172a'}}>
                        {entity.source} → {entity.target}
                      </div>
                      <div className="text-xs text-gray-500">
                        Entity: {entity.entity} | ID: {entity.id}
                      </div>
                    </div>
                    {selectedTargetPair?.id === entity.id && (
                      <Check className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button 
          onClick={() => setCurrentStep(2)} 
          disabled={!selectedSourcePair || !selectedTargetPair}
          className="bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          Next: Review Selection <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium" style={{color: '#0f172a'}}>Review Connector Pair Configuration</h3>
        <p className="text-sm text-gray-600 mt-1">
          Review the new connector pair that will be created using CSV reference logic
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selected Reference Pairs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm" style={{color: '#0f172a'}}>Selected Reference Pairs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-sm font-medium text-blue-800">Source Reference</div>
              <div className="text-sm text-blue-700">
                {selectedSourcePair?.source} → {selectedSourcePair?.target}
              </div>
              <div className="text-xs text-blue-600">ID: {selectedSourcePair?.id}</div>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-sm font-medium text-green-800">Target Reference</div>
              <div className="text-sm text-green-700">
                {selectedTargetPair?.source} → {selectedTargetPair?.target}
              </div>
              <div className="text-xs text-green-600">ID: {selectedTargetPair?.id}</div>
            </div>
          </CardContent>
        </Card>

        {/* New Connector Pair */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm" style={{color: '#0f172a'}}>New Connector Pair</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
              <div className="text-sm font-medium text-yellow-800">Will Create</div>
              <div className="text-lg font-semibold text-yellow-900 mt-1">
                {selectedSourcePair?.source} → {selectedTargetPair?.target}
              </div>
              <div className="text-sm text-yellow-700 mt-2">
                Using CSV reference logic to populate all database tables with appropriate values from the selected reference pairs.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SQL Generation Option */}
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={generateScriptsOnly}
              onCheckedChange={setGenerateScriptsOnly}
              className="border-orange-300 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
            />
            <div>
              <Label htmlFor="generateScriptsOnly" className="font-medium text-orange-800 cursor-pointer">
                Generate SQL Scripts Only (Recommended)
              </Label>
              <p className="text-sm text-orange-700">
                Create SQL INSERT scripts for manual review and execution instead of directly inserting into the database.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          Back
        </Button>
        <Button 
          onClick={buildConnectorPair} 
          disabled={loading} 
          className="bg-green-600 hover:bg-green-700 text-white transition-colors"
        >
          {loading ? 
            (generateScriptsOnly ? 'Generating Scripts...' : 'Creating...') : 
            (generateScriptsOnly ? 'Generate SQL Scripts' : 'Create Connector Pair')
          } 
          <Check className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium" style={{color: '#0f172a'}}>Generated SQL Scripts</h3>
        <p className="text-sm text-gray-600">Review and copy these SQL scripts to execute manually</p>
      </div>

      {generatedSqlScripts && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{color: '#0f172a'}}>
              <div>
                <span>SQL INSERT Scripts</span>
                <div className="text-sm font-normal text-gray-600">
                  {generatedSqlScripts.source_system} → {generatedSqlScripts.target_system}
                </div>
              </div>
              <div className="flex space-x-2 flex-shrink-0">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedSqlScripts.sql_scripts);
                    toast.success('SQL scripts copied to clipboard!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    const blob = new Blob([generatedSqlScripts.sql_scripts], { type: 'text/sql' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `connector_pair_${generatedSqlScripts.source_system}_${generatedSqlScripts.target_system}_${Date.now()}.sql`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('SQL scripts downloaded!');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="m-4 bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-[500px] overflow-auto border border-slate-600">
              <pre className="whitespace-pre-wrap break-words overflow-wrap-anywhere">{generatedSqlScripts.sql_scripts}</pre>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800">Instructions for Manual Execution</h4>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>1. Copy or download the SQL scripts above</li>
                <li>2. Connect to your PostgreSQL database using your preferred client</li>
                <li>3. Execute the scripts in the order they appear</li>
                <li>4. Verify the data was inserted correctly</li>
                <li>5. Refresh the application to see the new connector pair</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(2)}>
          Back
        </Button>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white" 
        style={{backgroundColor: 'white', color: '#0f172a'}}
      >
        <DialogHeader>
          <DialogTitle style={{color: '#0f172a'}}>
            <div className="flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Build Connector Pair - CSV Reference Logic</span>
            </div>
          </DialogTitle>
          <DialogDescription style={{color: '#64748b'}}>
            Create new connector pairs using existing pairs as templates with CSV reference logic
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="mt-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        <div className="flex justify-between pt-4 border-t mt-6">
          <Button variant="outline" onClick={() => {
            resetBuilder();
            onOpenChange(false);
          }}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          
          {currentStep > 1 && (
            <div className="text-sm text-gray-500">
              Step {currentStep} of 3
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

};

export default ConnectorPairBuilder;